import type { PublicFetcherOptions } from "../types/api";
import { getValidToken } from "./fetcher";

/**
 * Public fetcher - works with or without authentication
 * Sends token if available via MSAL, otherwise makes unauthenticated request
 * Used for endpoints that return different data based on authentication state
 * 
 * @param options - Fetcher options including url, method, body, etc.
 * @returns Promise with typed response data
 * @throws Error if request fails
 * 
 * @example
 * ```typescript
 * // Public view (no auth required)
 * const event = await publicFetcher<EventData>({ 
 *   url: '/event/123' 
 * });
 * 
 * // With auth (returns more data if authenticated)
 * const volunteer = await publicFetcher<VolunteerData>({ 
 *   url: '/volunteer/456' 
 * });
 * ```
 */
export const publicFetcher = async <T = unknown>({
  url,
  method = "GET",
  body,
  noContentType = false,
}: PublicFetcherOptions): Promise<T> => {
  const token = await getValidToken().catch(() => null);

  const headers = new Headers();
  
  // Add token if available (for authenticated requests)
  if (token) {
    headers.append("Authorization", `Bearer ${token}`);
  }
  
  if (!noContentType) {
    headers.append("Content-Type", "application/json");
  }

  const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
  const response = await fetch(`${apiBase}${url}`, {
    method,
    headers,
    body,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error ${response.status}: ${errorText}`);
  }

  const contentType = response.headers.get("Content-Type");

  // Handle different content types
  if (contentType?.includes("application/json")) {
    return response.json() as Promise<T>;
  }
  
  if (contentType?.includes("text/calendar")) {
    return response.text() as Promise<T>;
  }
  
  if (contentType?.includes("text/plain")) {
    return response.text() as Promise<T>;
  }
  
  if (contentType?.includes("application/octet-stream")) {
    return response.blob() as Promise<T>;
  }

  return response.text() as Promise<T>;
};

